"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VisualLog() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: galleryRef,
    isVisible: galleryVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const galleryItems = [{
    index: "01",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/hackathon_coding_session.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/hackathon_coding_session.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/hackathon_coding_session.png-wvc-sizes",
    alt: "Developers collaborating at a weekend hackathon",
    caption: "Hackathon Main Stage, Zone A",
    colClass: "lg:col-span-4 lg:row-span-2",
    aspect: "aspect-[3/4] lg:aspect-auto lg:h-full"
  }, {
    index: "02",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_passion_project.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_passion_project.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_passion_project.png-wvc-sizes",
    alt: "Member working on a custom mechanical keyboard project",
    caption: "Hardware Lab, Bench 02",
    colClass: "lg:col-span-4",
    aspect: "aspect-[4/3]"
  }, {
    index: "03",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/hardware_setup_macro.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/hardware_setup_macro.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/hardware_setup_macro.png-wvc-sizes",
    alt: "Close up of a custom server rack and wiring",
    caption: "Server Infrastructure, Node 04",
    colClass: "lg:col-span-4",
    aspect: "aspect-square"
  }, {
    index: "04",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/community_lounge_area.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/community_lounge_area.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/community_lounge_area.png-wvc-sizes",
    alt: "Members relaxing in the community lounge",
    caption: "Member Lounge, Ground Floor",
    colClass: "lg:col-start-5 lg:col-span-4",
    aspect: "aspect-[4/3]"
  }, {
    index: "05",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/coding_terminal_detail.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/coding_terminal_detail.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/coding_terminal_detail.webp-wvc-sizes",
    alt: "Macro detail of a terminal screen with code",
    caption: "Development Terminal, Lab 01",
    colClass: "lg:col-span-4",
    aspect: "aspect-[3/4]"
  }];
  return <section data-section-id="4048" id="visual-log" className="bg-background text-foreground py-24 md:py-32 lg:py-48">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={introRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-8 transition-all duration-1000 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-8">VISUAL LOG</p>
            <h2 className="font-default font-semibold text-foreground text-4xl md:text-5xl lg:text-[72px] leading-[1.05] tracking-tight">
              Building the future.{" "}
              <span className="italic font-light">One commit at a time.</span>
            </h2>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 lg:mt-20">
            <p className="font-default text-[18px] leading-relaxed text-foreground/80">
              At Code Hub, we transform ideas into reality through collaborative
              coding and hardware experimentation. Our space is designed for
              builders, featuring high-performance workstations, dedicated
              project benches, and a community-driven environment that fosters
              innovation &mdash; so every line of code is written with purpose,
              and every member finds the tools they need to succeed.
            </p>
            <div className="mt-10">
              <Link to="/JoinUsPage" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary">
                <span className="relative">
                  Download our toolkit
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-primary origin-left scale-x-100 transition-transform duration-500 ease-out group-hover:scale-x-0" />
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-primary origin-right scale-x-0 transition-transform duration-500 ease-out delay-100 group-hover:scale-x-100" />
                </span>
                <ArrowRight className="size-3.5 transition-transform duration-500 group-hover:translate-x-1 text-primary" />
              </Link>
            </div>
          </div>
        </div>

        {}
        <div ref={galleryRef} className="mt-24 md:mt-32 lg:mt-40 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
          {galleryItems.map((item, i) => <figure key={i} data-index={i} className={`group relative overflow-hidden bg-card rounded-lg ${item.colClass} ${item.aspect} transition-all duration-1000 ease-out ${galleryVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
          transitionDelay: galleryVisible ? `${i * 120}ms` : "0ms"
        }}>
              <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03] group-hover:saturate-150" />

              <span className="absolute top-5 left-5 font-mono text-[11px] uppercase tracking-[0.2em] text-white z-10">
                {item.index}
              </span>

              <figcaption className="absolute inset-x-0 bottom-0 p-5 pt-16 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 translate-y-2 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-y-0">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white">
                  {item.caption}
                </span>
              </figcaption>
            </figure>)}
        </div>
      </div>
    </section>;
}